'use client';

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, CheckCircle2, FileX, RefreshCw, Search, Shield, ShieldAlert, ShieldCheck, TrendingUp, AlertCircle, BarChart2, FileWarning, Eye } from 'lucide-react';
import { monitoringService, type DbMonitoringAssessment } from '@/lib/supabase/tprmService';
import { useRole } from '@/contexts/RoleContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type RiskTier = 'Critical' | 'High' | 'Moderate' | 'Low';

interface ReassessmentItem {
  id: string;
  vendorName: string;
  vendorId: string;
  riskTier: RiskTier;
  department: string;
  lastAssessment: string;
  dueDate: string;
  daysOverdue: number;
  assessmentType: string;
  assignedTo: string;
  status: 'Overdue' | 'Due Soon' | 'Scheduled' | 'In Progress';
}

interface ScorecardVendor {
  id: string;
  vendorName: string;
  riskTier: RiskTier;
  slaPerformance: number;
  insuranceStatus: 'Current' | 'Expiring Soon' | 'Expired' | 'Missing';
  complianceScore: number;
  securityRefresh: 'Current' | 'Due' | 'Overdue' | 'N/A';
  incidentCount: number;
  overallScore: number;
  trend: 'up' | 'down' | 'stable';
}

interface CalendarEvent {
  id: string;
  vendorName: string;
  riskTier: RiskTier;
  reviewType: string;
  scheduledDate: string;
  frequency: string;
  status: 'Upcoming' | 'Completed' | 'Missed';
}

interface MissingEvidence {
  id: string;
  vendorName: string;
  riskTier: RiskTier;
  evidenceType: string;
  requiredBy: string;
  daysOverdue: number;
  department: string;
  owner: string;
  severity: 'Critical' | 'High' | 'Moderate';
}

function dbToReassessment(d: DbMonitoringAssessment): ReassessmentItem {
  const status = d.days_overdue > 0 ? 'Overdue' :
    d.due_date && new Date(d.due_date) <= new Date(Date.now() + 14 * 86400000) ? 'Due Soon': d.assess_status as'Overdue' | 'Due Soon' | 'Scheduled' | 'In Progress';
  return {
    id: d.id,
    vendorName: d.vendor_name,
    vendorId: d.vendor_id || '',
    riskTier: d.risk_tier as RiskTier,
    department: d.department,
    lastAssessment: d.last_assessment || '',
    dueDate: d.due_date || '',
    daysOverdue: d.days_overdue,
    assessmentType: d.assessment_type,
    assignedTo: d.assigned_to,
    status,
  };
}

function dbToScorecard(d: DbMonitoringAssessment): ScorecardVendor {
  return {
    id: d.id,
    vendorName: d.vendor_name,
    riskTier: d.risk_tier as RiskTier,
    slaPerformance: d.sla_performance,
    insuranceStatus: d.insurance_status as ScorecardVendor['insuranceStatus'],
    complianceScore: d.compliance_score,
    securityRefresh: d.security_refresh as ScorecardVendor['securityRefresh'],
    incidentCount: d.incident_count,
    overallScore: d.overall_score,
    trend: d.trend as 'up' | 'down' | 'stable',
  };
}

// ─── Static calendar / evidence data (not yet in DB) ─────────────────────────

const CALENDAR_EVENTS: CalendarEvent[] = [
  { id: 'ce-001', vendorName: 'Axon Enterprise', riskTier: 'Critical', reviewType: 'Full Security Review', scheduledDate: '2026-04-02', frequency: 'Annual', status: 'Upcoming' },
  { id: 'ce-002', vendorName: 'Tyler Technologies', riskTier: 'Critical', reviewType: 'Quarterly SLA Review', scheduledDate: '2026-04-01', frequency: 'Quarterly', status: 'Upcoming' },
  { id: 'ce-003', vendorName: 'Motorola Solutions', riskTier: 'Critical', reviewType: 'Incident Follow-up', scheduledDate: '2026-03-28', frequency: 'Ad-hoc', status: 'Missed' },
  { id: 'ce-004', vendorName: 'Granicus', riskTier: 'High', reviewType: 'Semi-Annual Review', scheduledDate: '2026-04-10', frequency: 'Semi-Annual', status: 'Upcoming' },
  { id: 'ce-005', vendorName: 'Veolia Water', riskTier: 'High', reviewType: 'Operational Review', scheduledDate: '2026-04-05', frequency: 'Semi-Annual', status: 'Upcoming' },
  { id: 'ce-006', vendorName: 'Accela', riskTier: 'Moderate', reviewType: 'Annual Review', scheduledDate: '2026-09-15', frequency: 'Annual', status: 'Upcoming' },
  { id: 'ce-007', vendorName: 'Tyler Technologies', riskTier: 'Critical', reviewType: 'Security Patch Verification', scheduledDate: '2026-03-25', frequency: 'Quarterly', status: 'Completed' },
  { id: 'ce-008', vendorName: 'Cityworks', riskTier: 'Moderate', reviewType: 'Annual Review', scheduledDate: '2026-10-01', frequency: 'Annual', status: 'Upcoming' },
  { id: 'ce-009', vendorName: 'Comcast Business', riskTier: 'Low', reviewType: 'Biennial Review', scheduledDate: '2027-03-29', frequency: 'Biennial', status: 'Upcoming' },
];

const MISSING_EVIDENCE: MissingEvidence[] = [
  { id: 'me-001', vendorName: 'Axon Enterprise', riskTier: 'Critical', evidenceType: 'SOC 2 Type II Report', requiredBy: '2026-03-01', daysOverdue: 28, department: 'Police', owner: 'James Okafor', severity: 'Critical' },
  { id: 'me-002', vendorName: 'Motorola Solutions', riskTier: 'Critical', evidenceType: 'Penetration Test Results', requiredBy: '2026-02-15', daysOverdue: 42, department: 'Police', owner: 'James Okafor', severity: 'Critical' },
  { id: 'me-003', vendorName: 'Motorola Solutions', riskTier: 'Critical', evidenceType: 'Certificate of Insurance', requiredBy: '2026-03-20', daysOverdue: 9, department: 'Police', owner: 'Marcus Webb', severity: 'High' },
  { id: 'me-004', vendorName: 'Veolia Water', riskTier: 'High', evidenceType: 'Business Continuity Plan', requiredBy: '2026-03-15', daysOverdue: 14, department: 'Public Works', owner: 'Linda Torres', severity: 'High' },
  { id: 'me-005', vendorName: 'Granicus', riskTier: 'High', evidenceType: 'Data Processing Agreement', requiredBy: '2026-04-01', daysOverdue: 0, department: 'City Clerk', owner: 'Priya Nair', severity: 'Moderate' },
  { id: 'me-006', vendorName: 'Accela', riskTier: 'Moderate', evidenceType: 'Annual Security Questionnaire', requiredBy: '2026-04-15', daysOverdue: 0, department: 'Planning', owner: 'Sarah Chen', severity: 'Moderate' },
];

// ─── Frequency config ─────────────────────────────────────────────────────────

const FREQUENCY_CONFIG: Record<RiskTier, { label: string; color: string; bg: string; border: string; months: string }> = {
  Critical: { label: 'Critical', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950', border: 'border-red-200 dark:border-red-800', months: 'Every 3 months' },
  High:     { label: 'High',     color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950', border: 'border-orange-200 dark:border-orange-800', months: 'Every 6 months' },
  Moderate: { label: 'Moderate', color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950', border: 'border-amber-200 dark:border-amber-800', months: 'Annually' },
  Low:      { label: 'Low',      color: 'text-zinc-600 dark:text-zinc-400', bg: 'bg-zinc-50 dark:bg-zinc-800', border: 'border-zinc-200 dark:border-zinc-700', months: 'Every 2 years' },
};

// ─── Helper components ────────────────────────────────────────────────────────

function RiskBadge({ tier }: { tier: RiskTier }) {
  const map: Record<RiskTier, string> = {
    Critical: 'badge badge-critical',
    High: 'badge badge-high',
    Moderate: 'badge badge-moderate',
    Low: 'badge badge-low',
  };
  return <span className={map[tier]}>{tier}</span>;
}

function StatusDot({ status }: { status: string }) {
  const map: Record<string, string> = {
    Overdue: 'bg-red-500', 'Due Soon': 'bg-amber-500', 'In Progress': 'bg-blue-500',
    Scheduled: 'bg-zinc-400', Upcoming: 'bg-blue-500', Completed: 'bg-green-500', Missed: 'bg-red-500',
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status] ?? 'bg-zinc-400'}`} />;
}

function ScoreBar({ value, max = 100 }: { value: number; max?: number }) {
  const pct = (value / max) * 100;
  const color = pct >= 90 ? 'bg-green-500' : pct >= 75 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--border))]">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums font-medium text-[hsl(var(--text-primary))] w-8 text-right">{value}%</span>
    </div>
  );
}

function InsuranceBadge({ status }: { status: ScorecardVendor['insuranceStatus'] }) {
  const map: Record<string, string> = {
    Current: 'badge badge-active', 'Expiring Soon': 'badge badge-expiring',
    Expired: 'badge badge-critical', Missing: 'badge badge-critical',
  };
  return <span className={map[status] ?? 'badge'}>{status}</span>;
}

function SecurityBadge({ status }: { status: ScorecardVendor['securityRefresh'] }) {
  const map: Record<string, string> = {
    Current: 'badge badge-active', Due: 'badge badge-moderate',
    Overdue: 'badge badge-critical', 'N/A': 'badge badge-draft',
  };
  return <span className={map[status] ?? 'badge'}>{status}</span>;
}

// ─── Section: Reassessment Queue ──────────────────────────────────────────────

function ReassessmentQueue({ items }: { items: ReassessmentItem[] }) {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const statuses = ['All', 'Overdue', 'Due Soon', 'In Progress', 'Scheduled'];

  const filtered = items.filter(item => {
    const matchSearch = item.vendorName.toLowerCase().includes(search.toLowerCase()) ||
      item.department.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || item.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const overdue = items.filter(i => i.status === 'Overdue').length;
  const dueSoon = items.filter(i => i.status === 'Due Soon').length;

  return (
    <div className="card">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <RefreshCw size={15} className="text-[hsl(var(--accent))]" />
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Reassessment Queue</h2>
          <span className="badge badge-pending">{items.length} vendors</span>
          {overdue > 0 && <span className="badge badge-critical">{overdue} overdue</span>}
          {dueSoon > 0 && <span className="badge badge-moderate">{dueSoon} due soon</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
            <input className="input-base pl-7 h-7 text-xs w-44" placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex items-center gap-1">
            {statuses.map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} className={`px-2 py-1 text-2xs rounded font-medium transition-colors ${filterStatus === s ? 'bg-[hsl(var(--accent))] text-white' : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background))]'}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
              {['Vendor', 'Risk Tier', 'Department', 'Assessment Type', 'Last Assessment', 'Due Date', 'Overdue', 'Assigned To', 'Status', ''].map(h => (
                <th key={h} className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(item => (
              <tr key={item.id} className="border-b border-[hsl(var(--border))] table-row-hover">
                <td className="px-3 py-2.5">
                  <div className="font-medium text-[hsl(var(--text-primary))]">{item.vendorName}</div>
                  <div className="text-2xs text-[hsl(var(--text-muted))]">{item.vendorId}</div>
                </td>
                <td className="px-3 py-2.5"><RiskBadge tier={item.riskTier} /></td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))]">{item.department}</td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))]">{item.assessmentType}</td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-muted))] tabular-nums">{item.lastAssessment}</td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-muted))] tabular-nums">{item.dueDate}</td>
                <td className="px-3 py-2.5 tabular-nums">
                  {item.daysOverdue > 0 ? <span className="text-red-600 font-semibold">+{item.daysOverdue}d</span> : <span className="text-[hsl(var(--text-muted))]">—</span>}
                </td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))]">{item.assignedTo}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1.5 badge ${item.status === 'Overdue' ? 'badge-critical' : item.status === 'Due Soon' ? 'badge-moderate' : item.status === 'In Progress' ? 'badge-in-progress' : 'badge-draft'}`}>
                    <StatusDot status={item.status} />
                    {item.status}
                  </span>
                </td>
                <td className="px-3 py-2.5"><button className="btn-ghost py-1 px-2 text-2xs">Schedule</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-8 text-center text-sm text-[hsl(var(--text-muted))]">No items match your filters.</div>}
      </div>
    </div>
  );
}

// ─── Section: Vendor Scorecard ────────────────────────────────────────────────

function VendorScorecard({ vendors }: { vendors: ScorecardVendor[] }) {
  const [selectedTier, setSelectedTier] = useState<string>('All');
  const tiers = ['All', 'Critical', 'High', 'Moderate', 'Low'];
  const filtered = vendors.filter(v => selectedTier === 'All' || v.riskTier === selectedTier);

  return (
    <div className="card">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart2 size={15} className="text-[hsl(var(--accent))]" />
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Vendor Scorecard</h2>
          <span className="text-xs text-[hsl(var(--text-muted))]">SLA · Insurance · Compliance · Security · Incidents</span>
        </div>
        <div className="flex items-center gap-1">
          {tiers.map(t => (
            <button key={t} onClick={() => setSelectedTier(t)} className={`px-2 py-1 text-2xs rounded font-medium transition-colors ${selectedTier === t ? 'bg-[hsl(var(--accent))] text-white' : 'text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background))]'}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))]">Vendor</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))]">Risk Tier</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))] min-w-[140px]">SLA Performance</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))]">Insurance</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))] min-w-[140px]">Compliance</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))]">Security Refresh</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))]">Incidents</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))] min-w-[120px]">Overall Score</th>
              <th className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))]">Trend</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(v => (
              <tr key={v.id} className="border-b border-[hsl(var(--border))] table-row-hover">
                <td className="px-3 py-2.5 font-medium text-[hsl(var(--text-primary))] whitespace-nowrap">{v.vendorName}</td>
                <td className="px-3 py-2.5"><RiskBadge tier={v.riskTier} /></td>
                <td className="px-3 py-2.5"><ScoreBar value={v.slaPerformance} /></td>
                <td className="px-3 py-2.5"><InsuranceBadge status={v.insuranceStatus} /></td>
                <td className="px-3 py-2.5"><ScoreBar value={v.complianceScore} /></td>
                <td className="px-3 py-2.5"><SecurityBadge status={v.securityRefresh} /></td>
                <td className="px-3 py-2.5 text-center">
                  {v.incidentCount > 0 ? <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><AlertCircle size={11} />{v.incidentCount}</span> : <span className="text-[hsl(var(--positive))]"><CheckCircle2 size={13} /></span>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-[hsl(var(--border))]">
                      <div className={`h-2 rounded-full ${v.overallScore >= 90 ? 'bg-green-500' : v.overallScore >= 75 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${v.overallScore}%` }} />
                    </div>
                    <span className={`text-xs font-bold tabular-nums ${v.overallScore >= 90 ? 'text-green-600' : v.overallScore >= 75 ? 'text-amber-600' : 'text-red-600'}`}>{v.overallScore}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  {v.trend === 'up' && <TrendingUp size={14} className="text-green-500" />}
                  {v.trend === 'down' && <TrendingUp size={14} className="text-red-500 rotate-180" />}
                  {v.trend === 'stable' && <span className="text-[hsl(var(--text-muted))] text-xs">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Section: Monitoring Calendar ────────────────────────────────────────────

function MonitoringCalendar() {
  const upcoming = CALENDAR_EVENTS.filter(e => e.status === 'Upcoming');
  const completed = CALENDAR_EVENTS.filter(e => e.status === 'Completed');
  const missed = CALENDAR_EVENTS.filter(e => e.status === 'Missed');

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <div className="card p-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Calendar size={15} className="text-[hsl(var(--accent))]" />
          <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Risk-Based Frequencies</h3>
        </div>
        <p className="text-xs text-[hsl(var(--text-muted))] leading-relaxed">Monitoring cadence is determined by vendor risk tier. Higher-risk vendors require more frequent reviews.</p>
        <div className="space-y-2 mt-2">
          {(['Critical', 'High', 'Moderate', 'Low'] as RiskTier[]).map(tier => {
            const cfg = FREQUENCY_CONFIG[tier];
            const count = CALENDAR_EVENTS.filter(e => e.riskTier === tier).length;
            return (
              <div key={tier} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${cfg.bg} ${cfg.border}`}>
                <div className="flex items-center gap-2">
                  {tier === 'Critical' && <ShieldAlert size={13} className={cfg.color} />}
                  {tier === 'High' && <Shield size={13} className={cfg.color} />}
                  {(tier === 'Moderate' || tier === 'Low') && <ShieldCheck size={13} className={cfg.color} />}
                  <div>
                    <div className={`text-xs font-semibold ${cfg.color}`}>{tier}</div>
                    <div className={`text-2xs ${cfg.color} opacity-80`}>{cfg.months}</div>
                  </div>
                </div>
                <span className={`text-xs font-bold tabular-nums ${cfg.color}`}>{count} vendors</span>
              </div>
            );
          })}
        </div>
        <div className="pt-2 border-t border-[hsl(var(--border))] grid grid-cols-3 gap-2 text-center">
          <div><div className="text-lg font-bold text-blue-600 tabular-nums">{upcoming.length}</div><div className="text-2xs text-[hsl(var(--text-muted))]">Upcoming</div></div>
          <div><div className="text-lg font-bold text-green-600 tabular-nums">{completed.length}</div><div className="text-2xs text-[hsl(var(--text-muted))]">Completed</div></div>
          <div><div className="text-lg font-bold text-red-600 tabular-nums">{missed.length}</div><div className="text-2xs text-[hsl(var(--text-muted))]">Missed</div></div>
        </div>
      </div>
      <div className="xl:col-span-2 card">
        <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-[hsl(var(--accent))]" />
            <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Monitoring Calendar</h3>
          </div>
          <button className="btn-secondary text-xs"><Calendar size={12} /> Export Calendar</button>
        </div>
        <div className="divide-y divide-[hsl(var(--border))]">
          {CALENDAR_EVENTS.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)).map(event => (
            <div key={event.id} className="px-4 py-3 flex items-center gap-3 table-row-hover">
              <div className={`w-1 self-stretch rounded-full ${event.riskTier === 'Critical' ? 'bg-red-500' : event.riskTier === 'High' ? 'bg-orange-500' : event.riskTier === 'Moderate' ? 'bg-amber-500' : 'bg-zinc-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[hsl(var(--text-primary))]">{event.vendorName}</span>
                  <RiskBadge tier={event.riskTier} />
                </div>
                <div className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{event.reviewType} · {event.frequency}</div>
              </div>
              <div className="text-right">
                <div className="text-xs tabular-nums text-[hsl(var(--text-secondary))] font-medium">{event.scheduledDate}</div>
                <span className={`inline-flex items-center gap-1 badge mt-0.5 ${event.status === 'Upcoming' ? 'badge-in-progress' : event.status === 'Completed' ? 'badge-complete' : 'badge-critical'}`}>
                  <StatusDot status={event.status} />
                  {event.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Section: Missing Evidence Tracker ───────────────────────────────────────

function MissingEvidenceTracker() {
  const critical = MISSING_EVIDENCE.filter(e => e.severity === 'Critical').length;
  const high = MISSING_EVIDENCE.filter(e => e.severity === 'High').length;
  const { can } = useRole();
  return (
    <div className="card">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileWarning size={15} className="text-[hsl(var(--accent))]" />
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Missing Evidence Tracker</h2>
          <span className="badge badge-pending">{MISSING_EVIDENCE.length} items</span>
          {critical > 0 && <span className="badge badge-critical">{critical} critical</span>}
          {high > 0 && <span className="badge badge-high">{high} high</span>}
        </div>
        {can('monitoring:remind') && (
          <button className="btn-secondary text-xs"><FileX size={12} /> Send Reminders</button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]">
              {['Vendor', 'Risk Tier', 'Evidence Required', 'Department', 'Owner', 'Required By', 'Days Overdue', 'Severity', ''].map(h => (
                <th key={h} className="px-3 py-2 text-left font-semibold text-[hsl(var(--text-muted))] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MISSING_EVIDENCE.sort((a, b) => b.daysOverdue - a.daysOverdue).map(item => (
              <tr key={item.id} className="border-b border-[hsl(var(--border))] table-row-hover">
                <td className="px-3 py-2.5 font-medium text-[hsl(var(--text-primary))]">{item.vendorName}</td>
                <td className="px-3 py-2.5"><RiskBadge tier={item.riskTier} /></td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))]">{item.evidenceType}</td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))]">{item.department}</td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-secondary))]">{item.owner}</td>
                <td className="px-3 py-2.5 text-[hsl(var(--text-muted))] tabular-nums">{item.requiredBy}</td>
                <td className="px-3 py-2.5 tabular-nums">
                  {item.daysOverdue > 0 ? <span className="text-red-600 font-semibold">+{item.daysOverdue}d</span> : <span className="text-[hsl(var(--text-muted))]">Pending</span>}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`badge ${item.severity === 'Critical' ? 'badge-critical' : item.severity === 'High' ? 'badge-high' : 'badge-moderate'}`}>{item.severity}</span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    {can('monitoring:remind') && (
                      <button className="btn-ghost py-1 px-2 text-2xs">Remind</button>
                    )}
                    {can('monitoring:upload') && (
                      <button className="btn-ghost py-1 px-2 text-2xs">Upload</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function MonitoringScreen() {
  const [dbAssessments, setDbAssessments] = useState<DbMonitoringAssessment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    monitoringService.getAll().then((data) => {
      if (!cancelled) {
        setDbAssessments(data);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    const unsubscribe = monitoringService.subscribeToAll((updated) => {
      if (!cancelled) setDbAssessments(updated);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const reassessmentItems = dbAssessments.map(dbToReassessment);
  const scorecardVendors = dbAssessments.map(dbToScorecard);

  const overdueCount = reassessmentItems.filter(i => i.status === 'Overdue').length;
  const missingEvidenceCount = MISSING_EVIDENCE.length;
  const lowScoreCount = scorecardVendors.filter(v => v.overallScore < 75).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 gap-3">
        <RefreshCw size={20} className="animate-spin text-[hsl(var(--accent))]" />
        <p className="text-sm text-[hsl(var(--text-muted))]">Loading monitoring data…</p>
      </div>
    );
  }

  return (
    <div className="px-5 py-4 max-w-screen-2xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">Monitoring</h1>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">City of Pleasanton — Continuous Vendor Monitoring & Reassessment · As of Mar 29, 2026</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-2xs text-[hsl(var(--text-muted))] px-2 py-1 rounded border border-[hsl(var(--border))]">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            Live · Real-time
          </span>
          <button className="btn-secondary">Export Report</button>
          <button className="btn-primary"><RefreshCw size={12} /> Schedule Review</button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Vendors Monitored', value: scorecardVendors.length, icon: Eye, color: 'text-[hsl(var(--accent))]', bg: 'bg-[hsl(var(--accent-light))]' },
          { label: 'Reassessments Overdue', value: overdueCount, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950' },
          { label: 'Missing Evidence Items', value: missingEvidenceCount, icon: FileX, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950' },
          { label: 'Vendors Below Threshold', value: lowScoreCount, icon: ShieldAlert, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-950' },
        ].map(kpi => (
          <div key={kpi.label} className="card px-4 py-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center flex-shrink-0`}>
              <kpi.icon size={16} className={kpi.color} />
            </div>
            <div>
              <div className={`text-2xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</div>
              <div className="text-2xs text-[hsl(var(--text-muted))] leading-tight">{kpi.label}</div>
            </div>
          </div>
        ))}
      </div>

      <ReassessmentQueue items={reassessmentItems} />
      <VendorScorecard vendors={scorecardVendors} />

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar size={15} className="text-[hsl(var(--accent))]" />
          <h2 className="text-sm font-semibold text-[hsl(var(--text-primary))]">Monitoring Calendar</h2>
        </div>
        <MonitoringCalendar />
      </div>

      <MissingEvidenceTracker />
    </div>
  );
}
